<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template>
<div class="k-favorites-dialog-outer" @click="$emit('dialog_cancel')">
	<!-- <div class="k-favorites-dialog"> -->
		<div class="k-favorites-dialog-inner xelevation-3"
			:style="inner_style"
		>

			<div v-if="!lti_chooser" class="k-favorites-icon-holder">
				<k-icon small>{{icon}}</k-icon>
			</div>

			<DailyLessonsBtn v-if="$store.getters.show_daily_lessons && !lti_chooser" class="mb-6" />

			<div v-if="signed_in && favorite_collections.length==0" class="mb-3">
				<div v-if="studentish_role">
					<div v-if="$store.state.term_settings.block_sis_data_for_students"><i>You will be able to see {{role=='student'?'your':'your student’s'}} courses here once the school year begins.</i></div>
					<div v-else><i>According to the student information system, {{role=='student'?'you are not':'your student is not'}} currently enrolled in any courses.</i></div>
				</div>
				<div v-else>
					<i>You do not have any Favorite Collections.</i>
					<div v-show="false" class="mt-2" style="font-size:.85em">You can browse collections by clicking one of the tabs above;<br>you can then add collections to your Favorites from there.</div>
				</div>
			</div>

			<div v-if="signed_in && favorite_collections.length>0" class="mb-3">
				<div class="k-favorites-header">
					{{favorites_header_text}}
				</div>

				<div v-if="!lti_chooser && user_info.role == 'parent'">
					<div class="text-center" 
						v-if="child_count == 0"
					>You do not appear to be connected to any students in your school’s student information system.</div>

					<div v-else class="d-flex justify-center mt-2">
						<span>Viewing courses for student <b class="ml-1">{{ child_data_showing.first_name }} {{ child_data_showing.last_name }}</b></span>
						<k-menu bottom left v-model="child_menu"><template v-slot:activator="{ on }"><k-btn v-if="child_count > 1" small v-on="on" class="ml-2" color="secondary"><k-icon small class="mr-2">fas fa-arrow-right-arrow-left</k-icon>Switch To Student…</k-btn></template>
							<k-list>
								<k-list-item v-for="(child_data, child_email) in user_info.child_data" :key="child_email" 
									@click.stop="switch_child_data_showing(child_email)"
								>
									<k-list-item-title>{{ child_data.first_name }} {{ child_data.last_name }} ({{ child_email }})</k-list-item-title>
								</k-list-item>
							</k-list>
						</k-menu>
					</div>
				</div>

				<div class="d-flex flex-wrap justify-center mt-2">
					<FavoriteTile v-for="(collection) in favorite_collections" 
						:key="collection.class_sourcedIds ? collection.class_sourcedIds[0] : collection.course_code"
						:course="collection"
						:welcome_section_collection_types="['course', 'repo', 'pd', 'my']"
						:course_showing="''"
						@show_course="show_course"
					></FavoriteTile>
				</div>
			</div>

			<div v-if="recent_collections.length>0">
				<div class="k-favorites-header">
					{{lti_chooser ? 'Browse' : ''}} Recent Collections
				</div>

				<div class="k-favorites-recent-tiles d-flex flex-wrap justify-center mt-2">
					<FavoriteTile v-for="(collection) in recent_collections" :key="collection.course_code"
						:course="collection"
						:welcome_section_collection_types="['course', 'repo', 'pd', 'my']"
						:course_showing="''"
						@show_course="show_course"
					></FavoriteTile>
				</div>
			</div>

			<div v-else-if="!lti_chooser" class="text-center">
				<i>Recently-visited content collections will appear here.</i>
			</div>
		</div>
	<!-- </div> -->
</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import FavoriteTile from './FavoriteTile'
import DailyLessonsBtn from '../home/DailyLessonsBtn'

export default {
	components: { FavoriteTile },
	props: {
		// req: { type: String, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		child_menu: false,
	}},
	computed: {
		...mapState(['user_info', 'all_courses', 'sis_classes', 'added_my_courses', 'removed_my_courses', 'site_config']),
		...mapGetters(['signed_in', 'child_count', 'child_data_showing', 'role', 'manage_assignments', 'use_message_center', 'studentish_role', 'my_default_collection', 'lti_chooser', 'favorite_collections']),

		favorites_header_text() {
			if (this.user_info.role === 'parent') return "My Child’s Courses"
			if (this.lti_chooser) return 'Browse Your Favorite Collections'
			return 'Your Favorite Collections'
		},

		icon() {
			return this.site_config.customized_tabs.favorites?.icon || 'fas fa-bookmark'
		},

		inner_style() {
			let margin = 48	// this is k-favorites-dialog-inner: padding * 2 + border * 2
			let favorite_width = 184	// this is k-cv-lp-tile: the width itself + 2 * the margin
			let recent_width = 144	// this is recent-tiles.k-cv-lp-tile: the width itself + 2 * the margin

			let fw = (this.favorite_collections.length == 0) ? 0 : margin + favorite_width * this.favorite_collections.length
			let rw = (this.recent_collections.length == 0) ? 0 : margin + recent_width * this.recent_collections.length

			if (fw < 600 && rw < 600) return 'width: 600px'
			else if (fw > rw) return `width:${fw}px`
			else return `width:${rw}px`
		},

		last_collections_viewed: {
			get() { return this.$store.state.lst.last_collections_viewed },
			set(val) { this.$store.commit('lst_set', ['last_collections_viewed', val]) }
		},
		recent_collections() {
			if (this.user_info.role == 'parent') return []

			let arr = []

			// get the last X collections the user has viewed (that aren't in favorite collections??)
			for (let course_code of this.last_collections_viewed) {
				if (!this.my_default_collection || course_code != this.my_default_collection.course_code) {		// but not the default collection
					let c = this.all_courses.find(x=>x.course_code == course_code)
					if (empty(c)) continue
					if (c.active == 'no' && !c.user_can_view_lp()) {
						// console.warn('user can’t view ' + c.title)
						continue
					}
					arr.push(c)
				}
			}

			// console.log('collections_list', arr)
			return arr

			// NOTE: sis_classes will appear first, in alpha order, followed by added collections, followed by recently-visited collections, with the default collection at the end
		},
		child_email_showing: {
			get() { return this.$store.state.lst.child_email_showing },
			set(val) { this.$store.commit('lst_set', ['child_email_showing', val]) }
		},
	},
	watch: {
	},
	created() {
	},
	mounted() {
	},
	methods: {
		show_course(course, event, unit_id) {
			console.warn('favorites show_course')
			if (empty(unit_id)) unit_id = 0

			// DEBUG: if you hold down the (mac) command and option key while clicking, just show the course data in the console
			if (event.metaKey && event.altKey) {
				this.log_object(course)
				return
			}
			// for legacy reasons the course will not be a proper Learning_Progression object; look up the proper lp
			let lp = this.all_courses.find(x=>x.course_code == course.course_code)

			let path = lp.vue_route(unit_id)
			if (path != this.$router.currentRoute.path) {
				this.$router.push({ path: path })
			}

			// in lti_chooser mode, emit event, which will show the collection
			if (this.lti_chooser) {
				this.$emit('go_to_collection', lp)
			}
		},

		log_object(o) {
			let s = ''
			for (let key in o) {
				s += sr('$1: $2\n', key, JSON.stringify(o[key]))
			}
			console.log(s)
		},

		switch_child_data_showing(child_email) {
			this.child_email_showing = child_email
			this.child_menu = false
		},
	}
}
</script>

<style lang="scss">
.k-favorites-dialog-outer {
	position:fixed;
	z-index:19;
	top:48px;
	left:0;
	width:100vw;
}

.k-favorites-dialog {
	width:auto;
	max-width:$k-max-content-width;
	max-width:800px;
	margin:0 auto;
}

.k-favorites-dialog-inner {
	position:relative;

	// width:800px;
	// max-width:$k-max-content-width;
	max-width:calc(100vw - 64px);
	// max-width:900px;
	background-color:#1D48C0;
	// background-color:rgb(29, 72, 192, 0.95);
	// background-color:rgba(240,240,240,0.97);
	// background-color:rgba(20,20,20,0.97);
	// background-color:rgba(29,72,192,0.9);
	// background-color:rgba(20, 50, 134, 0.9);
	color:#fff;
	border-radius:0 0 10px 10px;
	border:4px solid #fff;
	border-top:0;
	padding:20px;
	// margin-left:-20px;
	// margin-right:-20px;
	margin-left:32px;
	// box-shadow:0px 3px 13px 0px #000;
	// box-shadow: 0 10px 3px -5px rgba(0, 0, 0, 0.95);
	// box-shadow: 0 10px 10px -5px rgba(0, 0, 0, 0.5);
	box-shadow: 
		-8px 10px 10px -4px rgba(0, 0, 0, 0.2),  /* Left side shadow */
		8px 10px 10px -4px rgba(0, 0, 0, 0.2),   /* Right side shadow */
		0 12px 12px -4px rgba(0, 0, 0, 0.25);    /* Bottom side shadow */

	.k-favorites-icon-holder {
		position:absolute;
		left:24px;
		top:16px;
		.v-icon {
			color:#fff;
			font-size:40px!important;
		}
	}

	.k-favorites-header {
		font-size: 24px;
		font-weight:900;
		text-align:center;
	}

	// see styles for tiles below
}

// this alters the styles when we're showing the favorites in the LTI chooser
.k-lti-chooser.k-main-wrapper {
	.k-favorites-dialog-outer {
		position:relative;
		z-index:1;
		top:unset;
		left:unset;
	}
	.k-favorites-dialog {
		max-width:none;
	}
	.k-favorites-dialog-inner {
		max-width:none;
		margin:0 12px!important;
		width:auto!important;
		box-shadow:none;
		background-color: transparent!important;
		border:none!important;
	}
}

// special styles for tiles, either within the favorites "dialog" or the LTI chooser
.k-favorites-dialog-inner, .k-lti-chooser.k-main-wrapper {
	.k-cv-lp-tile {
		width:160px;
		height:160px;
		border-radius:24px;
		text-align:left;
		padding:0;
		margin:12px;
		flex-direction: row;
		align-items: center;
		cursor:pointer;
		position:relative;

		.k-cv-lp-tile__title {
			flex:1 1 160px;
			display:flex;
			flex-direction: column;
			justify-content: start;
			height:160px;
			border-radius:24px;
			font-size:18px;
			line-height:22px;
			font-weight:bold;
			color:#fff;
			padding:42px 12px 12px 12px;

			.v-icon {
				position:absolute;
				left:12px;
				top:12px;
				font-size:24px!important;
			}

			.k-cv-lp-tile__title-teacher {
				font-size:12px;
				line-height:17px;
				font-weight:normal;
				padding-top:4px;
			}
		}
	}

	.k-favorites-recent-tiles {
		.k-cv-lp-tile {
			width:128px;
			height:128px;
			border-radius:18px;
			margin:8px;
			.k-cv-lp-tile__title {
				flex:1 1 128px;
				height:128px;
				border-radius:18px;
				font-size:15px;
				line-height:18px;
				padding:42px 12px 12px 12px;
			}
		}

		.k-main-collection-item.k-cv-lp-tile.k-lp-tile-long-title .k-cv-lp-tile__title{
			font-size: 13px!important;
			line-height: 16px!important;
		}

		.k-main-collection-item.k-cv-lp-tile.k-lp-tile-extra-long-title .k-cv-lp-tile__title{
			font-size: 11px!important;
			line-height: 14px!important;
		}
	}
}
</style>